import { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Megaphone } from "lucide-react";
import { AccountShell, StatusPill, FileUpload } from "../components/AccountShell";
import { Input, Textarea, SubmitButton } from "../components/RegisterLayout";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { formatINR } from "../lib/format";

const EMPTY = {
  rfq_ref: "", rfq_id: "", material: "", grade: "", quantity: "", unit: "", unit_price: "",
  total: "", delivery_timeline: "", validity: "", payment_terms: "", remarks: "", document_url: "",
};

export default function SellerQuotations() {
  const { user, api } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    try { setQuotes((await api.get("/quotations/mine")).data); } catch { /* ignore */ }
  }, [api]);
  const loadRfqs = useCallback(async () => {
    try { setRfqs((await api.get("/rfqs/open")).data); } catch { /* ignore */ }
  }, [api]);
  useEffect(() => { load(); loadRfqs(); }, [load, loadRfqs]);

  const quoteThis = (r) => {
    setForm((f) => ({
      ...f,
      rfq_ref: r.ref,
      rfq_id: r.id,
      material: r.material || f.material,
      grade: r.grade || f.grade,
      quantity: r.quantity || f.quantity,
      unit: r.unit || f.unit,
    }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    toast.success(`Quoting requirement ${r.ref}`);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "unit_price" || name === "quantity") {
        const up = parseFloat(name === "unit_price" ? value : next.unit_price) || 0;
        const q = parseFloat(name === "quantity" ? value : next.quantity) || 0;
        if (up && q) next.total = Math.round(up * q);
      }
      return next;
    });
  };

  if (user === null) return <div className="min-h-screen bg-[#070707]" />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.account_type !== "seller") return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotations", {
        rfq_ref: form.rfq_ref,
        rfq_id: form.rfq_id,
        material: form.material,
        grade: form.grade,
        quantity: form.quantity,
        unit: form.unit,
        unit_price: parseFloat(form.unit_price) || 0,
        total: parseFloat(form.total) || 0,
        delivery_timeline: form.delivery_timeline,
        validity: form.validity,
        payment_terms: form.payment_terms,
        remarks: form.remarks,
        document_url: form.document_url,
      });
      toast.success("Quotation submitted. Our trade desk will review it shortly.");
      setForm(EMPTY); setDoc(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit quotation.");
    } finally { setLoading(false); }
  };

  return (
    <AccountShell
      kicker="/ Seller · Quotations"
      title="Submit a quotation"
      subtitle="Respond to a requirement or a demand raised by a Vinci buyer. Complete the quotation form below and track its status as our trade desk reviews it."
    >
      <Toaster position="bottom-right" theme="dark" richColors />

      {rfqs.length > 0 && (
        <div className="mb-14" data-testid="open-rfqs">
          <div className="flex items-center gap-3 mb-6">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[#B87333] text-black"><Megaphone size={16} /></span>
            <h2 className="font-serif text-2xl md:text-3xl">Open requirements <span className="text-[#8A8A82] text-lg">({rfqs.length})</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {rfqs.map((r) => (
              <div key={r.id} data-testid="rfq-card" className="rounded-sm border border-white/10 p-6 hover:border-[#B87333]/50 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{r.ref}</span>
                  <span className="font-body text-xs text-[#8A8A82]">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="font-serif text-xl mt-4">{r.title}</div>
                <p className="font-body text-sm text-[#8A8A82] mt-2">
                  {r.material}{r.grade ? ` · ${r.grade}` : ""} · {r.quantity} {r.unit || ""}
                  {r.destination_state ? ` · ${r.destination_state}` : ""}{r.target_delivery ? ` · ${r.target_delivery}` : ""}
                </p>
                {r.notes && <p className="font-body text-xs text-[#6f6f68] mt-2 leading-relaxed">{r.notes}</p>}
                <button data-testid={`quote-this-${r.id}`} onClick={() => quoteThis(r)} className="mt-5 rounded-full bg-[#F5F5F0] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">Quote this →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <motion.form
        ref={formRef}
        data-testid="quotation-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="grid md:grid-cols-2 gap-8">
          <Input label="RFQ / requirement reference" testid="quote-rfq" name="rfq_ref" required value={form.rfq_ref} onChange={onChange} />
          <Input label="Material" testid="quote-material" name="material" required value={form.material} onChange={onChange} />
          <Input label="Grade / specification" testid="quote-grade" name="grade" value={form.grade} onChange={onChange} />
          <Input label="Quantity" testid="quote-quantity" name="quantity" placeholder="e.g. 1000" required value={form.quantity} onChange={onChange} />
          <Input label="Unit" testid="quote-unit" name="unit" placeholder="e.g. MT / bags" value={form.unit} onChange={onChange} />
          <Input label="Quoted unit price (₹)" testid="quote-unit-price" name="unit_price" type="number" min="0" required value={form.unit_price} onChange={onChange} />
          <Input label="Total value (₹)" testid="quote-total" name="total" type="number" min="0" value={form.total} onChange={onChange} />
          <Input label="Delivery timeline" testid="quote-delivery" name="delivery_timeline" placeholder="e.g. 15 days ex-works" required value={form.delivery_timeline} onChange={onChange} />
          <Input label="Quote validity" testid="quote-validity" name="validity" placeholder="e.g. 7 days" value={form.validity} onChange={onChange} />
          <Input label="Payment terms" testid="quote-payment" name="payment_terms" placeholder="e.g. 50% advance" value={form.payment_terms} onChange={onChange} />
          <Textarea label="Remarks" testid="quote-remarks" name="remarks" rows={2} value={form.remarks} onChange={onChange} className="md:col-span-2" />
          <div className="md:col-span-2">
            <FileUpload
              testid="quote-file"
              doc={doc}
              onUploaded={(d, err) => {
                if (err) { toast.error(err); return; }
                setDoc(d); setForm((f) => ({ ...f, document_url: d.url }));
                toast.success("Document attached.");
              }}
            />
          </div>
        </div>
        <div className="mt-10">
          <SubmitButton testid="quote-submit" type="submit" disabled={loading}>
            {loading ? "Submitting…" : "Submit quotation"}
          </SubmitButton>
        </div>
      </motion.form>

      <div className="mt-20">
        <h2 className="font-serif text-2xl md:text-3xl mb-6">
          Your quotations <span className="text-[#8A8A82] text-lg">({quotes.length})</span>
        </h2>
        {quotes.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="no-quotations">No quotations submitted yet.</p>
        ) : (
          <div className="space-y-4" data-testid="quotations-list">
            {quotes.map((q) => (
              <div key={q.id} data-testid="quotation-row" className="border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg">{q.rfq_ref}</span>
                    <StatusPill status={q.status} testid={`quote-status-${q.id}`} />
                  </div>
                  <span className="font-body text-xs text-[#8A8A82]">{new Date(q.created_at).toLocaleDateString()}</span>
                </div>
                <div className="font-body text-sm text-[#8A8A82] mt-3">
                  {q.material}{q.grade ? ` · ${q.grade}` : ""} · {q.quantity} {q.unit} @ {formatINR(q.unit_price)} · Total <span className="text-[#2FB999]">{formatINR(q.total)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AccountShell>
  );
}
